import { createContext, useContext, useEffect, useState } from 'react';
import { fetchContent } from '../api/content';

/**
 * Loads the site content once and hands it to every component below.
 *
 * There is exactly one request for the whole app, made here, because every page
 * wants some part of the same payload and three components each fetching on
 * mount would be three round trips for one screen.
 */

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
	const [state, setState] = useState({ status: 'loading', data: null, error: null });

	useEffect(() => {
		const controller = new AbortController();

		fetchContent(controller.signal)
			.then((data) => setState({ status: 'ready', data, error: null }))
			.catch((error) => {
				// An abort is React tearing the component down, not a failure.
				if (error.name === 'AbortError') {
					return;
				}

				setState({ status: 'error', data: null, error });
			});

		return () => controller.abort();
	}, []);

	// The state object itself is the context value. No memo: useState already
	// hands back the same object until something actually changes, and wrapping
	// it in useMemo(() => state, [state]) recomputes on exactly the same
	// schedule — it is ceremony, not caching.
	return <ContentContext.Provider value={state}>{children}</ContentContext.Provider>;
}

/**
 * The whole content payload, plus its loading state.
 */
export function useContent() {
	const context = useContext(ContentContext);

	if (!context) {
		throw new Error('useContent must be used inside a ContentProvider.');
	}

	return context;
}

/**
 * The company details on their own, which is what most components want.
 *
 * Returns an empty object while loading so callers can read company.phone
 * without guarding every single access.
 */
export function useCompany() {
	return useContent().data?.company ?? {};
}

/**
 * The seven services.
 */
export function useServices() {
	return useContent().data?.services ?? [];
}

/**
 * The gallery categories and photos.
 */
export function useGallery() {
	return useContent().data?.gallery ?? { categories: [], photos: [] };
}
